// Unread divider — per-conversation last-read marker stored client-locally
// (localStorage, key `lastRead:<conversationId>`, value = the newest message's
// createdDate). On conversation open the divider anchors BEFORE the oldest
// message newer than the stored marker that is NOT authored by the user; it
// stays there for the viewing session while the marker advances to the newest
// message, so the next open is clean. No divider when nothing is unread — and
// no divider on the very first open (no marker yet). No backend.

export const lastReadKey = (conversationId: string): string => `lastRead:${conversationId}`;

type DividerMessage = {
  id?: string;
  role?: string;
  createdDate?: string;
};

const stamp = (createdDate: string | undefined): number => {
  if (!createdDate) return NaN;
  const t = Date.parse(createdDate);
  return Number.isNaN(t) ? NaN : t;
};

// The id of the message the divider renders before, or null when nothing is
// unread. Optimistic rows (no createdDate) never anchor it.
export function computeUnreadDividerId(messages: DividerMessage[], marker: string | null | undefined): string | null {
  if (!marker) return null;
  const markerTime = stamp(marker);
  if (Number.isNaN(markerTime)) return null;
  for (const m of messages) {
    if (m.role === 'USER') continue;
    if (!m.id) continue;
    const t = stamp(m.createdDate);
    if (!Number.isNaN(t) && t > markerTime) return m.id;
  }
  return null;
}

// The newest createdDate in the thread — the value the marker advances to.
// Null when no message carries a parseable date (nothing worth recording).
export function newestMessageStamp(messages: DividerMessage[]): string | null {
  let best: string | null = null;
  let bestTime = -Infinity;
  for (const m of messages) {
    const t = stamp(m.createdDate);
    if (!Number.isNaN(t) && t > bestTime) {
      bestTime = t;
      best = m.createdDate ?? null;
    }
  }
  return best;
}

export function readLastRead(conversationId: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(lastReadKey(conversationId));
  } catch {
    return null;
  }
}

export function writeLastRead(conversationId: string, createdDate: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(lastReadKey(conversationId), createdDate);
  } catch {
    // Storage unavailable (private mode quota) — the divider just won't persist.
  }
}
